import React, { useState, useEffect, useRef, useCallback } from 'react';
import { makeRequest } from '../../utils/httpRequest';
import { getAuthToken } from '../../utils/auth';
import Config from '../../core/config';
import { usePermission } from '../../core/PermissionContext';
import { UserPermission } from '../../utils/utils';
import './WhatsappPage.css';

const api = Config.apiUrl;

// ─── helpers ────────────────────────────────────────────────────────────────

function formatTime(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
}

function avatarColor(phone) {
    const colors = ['#1C6E47', '#0D5C6E', '#5C1C6E', '#6E1C1C', '#1C406E', '#6E4C1C'];
    let h = 0;
    for (let i = 0; i < phone.length; i++) h = (h * 31 + phone.charCodeAt(i)) >>> 0;
    return colors[h % colors.length];
}

// ─── ChatItem ────────────────────────────────────────────────────────────────

function ChatItem({ chat, active, onClick }) {
    return (
        <div className={`wa-chat-item ${active ? 'active' : ''}`} onClick={onClick}>
            <div className="wa-avatar" style={{ background: avatarColor(chat.phone) }}>
                {getInitials(chat.contactName || chat.phone)}
            </div>
            <div className="wa-chat-item-body">
                <div className="wa-chat-item-top">
                    <span className="wa-chat-name">{chat.contactName || chat.phone}</span>
                    <span className="wa-chat-time">{formatTime(chat.lastMessageAt)}</span>
                </div>
                <div className="wa-chat-item-bottom">
                    <span className="wa-chat-preview">{chat.lastMessageText || '–'}</span>
                    {chat.unreadCount > 0 && (
                        <span className="wa-badge">{chat.unreadCount > 99 ? '99+' : chat.unreadCount}</span>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── MessageBubble ────────────────────────────────────────────────────────────

function MediaContent({ msg }) {
    const token = getAuthToken();
    const src = `${Config.apiUrl}/api/whatsapp/media/${msg.mediaId}?auth=${token}`;
    const [lightbox, setLightbox] = useState(false);

    const download = useCallback(async (filename) => {
        try {
            const res = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error('Falha ao baixar');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            alert('Erro ao baixar o arquivo.');
        }
    }, [src, token]);

    if (msg.messageType === 'image') {
        return (
            <>
                <div className="wa-media-wrap">
                    <img
                        src={src}
                        alt="Imagem"
                        className="wa-media-img"
                        onClick={() => setLightbox(true)}
                        title="Clique para ampliar"
                    />
                    <button className="wa-dl-btn" onClick={() => download('imagem.jpg')}>
                        ⬇ Baixar
                    </button>
                </div>

                {lightbox && (
                    <div className="wa-lightbox" onClick={() => setLightbox(false)}>
                        <div className="wa-lightbox-inner" onClick={e => e.stopPropagation()}>
                            <button className="wa-lightbox-close" onClick={() => setLightbox(false)}>✕</button>
                            <img src={src} alt="Imagem" />
                            <button className="wa-dl-btn" onClick={() => download('imagem.jpg')}>
                                ⬇ Baixar imagem
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    }

    if (msg.messageType === 'video') {
        return (
            <div className="wa-media-wrap">
                <video src={src} controls className="wa-media-video" />
                <button className="wa-dl-btn" onClick={() => download('video.mp4')}>
                    ⬇ Baixar vídeo
                </button>
            </div>
        );
    }

    if (msg.messageType === 'audio') {
        return <audio src={src} controls className="wa-media-audio" />;
    }

    if (msg.messageType === 'document') {
        return (
            <button className="wa-media-doc" onClick={() => download(msg.text || 'documento')}>
                📎 {msg.text || 'Documento'}
            </button>
        );
    }

    return null;
}

function MessageBubble({ msg }) {
    const isOut = msg.direction === 'OUT';
    const hasMedia = msg.mediaId && msg.messageType !== 'text';
    return (
        <div className={`wa-msg-row ${isOut ? 'out' : 'in'}`}>
            <div className={`wa-bubble ${isOut ? 'out' : 'in'}`}>
                {isOut && msg.sentByUserName && (
                    <strong className="wa-msg-attendant">Atendente {msg.sentByUserName}</strong>
                )}
                {hasMedia && <MediaContent msg={msg} />}
                {msg.text && msg.messageType === 'text' && (
                    <span className="wa-msg-text">{msg.text}</span>
                )}
                {msg.text && hasMedia && msg.messageType !== 'document' && (
                    <span className="wa-msg-caption">{msg.text}</span>
                )}
                <div className="wa-msg-meta">
                    <span className="wa-msg-time">{formatTime(msg.timestamp)}</span>
                </div>
            </div>
        </div>
    );
}

// ─── MessageInput ─────────────────────────────────────────────────────────────

const ACCEPT_MEDIA = 'image/*,video/*,audio/*,application/pdf';

function MessageInput({ text, onTextChange, onKeyDown, onSend, onSendMedia, sending }) {
    const [mediaFile, setMediaFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const fileRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setMediaFile(file);

        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (ev) => setPreview({ type: 'image', src: ev.target.result, name: file.name });
            reader.readAsDataURL(file);
        } else if (file.type.startsWith('video/')) {
            setPreview({ type: 'video', name: file.name });
        } else if (file.type.startsWith('audio/')) {
            setPreview({ type: 'audio', name: file.name });
        } else {
            setPreview({ type: 'doc', name: file.name });
        }
    };

    const handleCancel = () => {
        setMediaFile(null);
        setPreview(null);
        if (fileRef.current) fileRef.current.value = '';
    };

    const handleSubmit = () => {
        if (sending) return;
        if (mediaFile) {
            onSendMedia(mediaFile);
            handleCancel();
        } else if (text.trim()) {
            onSend();
        }
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !mediaFile) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className="wa-input-bar">
            {preview && (
                <div className="wa-media-preview">
                    {preview.type === 'image' && <img src={preview.src} alt={preview.name} />}
                    {preview.type === 'video' && <span>🎬 {preview.name}</span>}
                    {preview.type === 'audio' && <span>🎵 {preview.name}</span>}
                    {preview.type === 'doc'   && <span>📎 {preview.name}</span>}
                    <button className="wa-media-preview-cancel" onClick={handleCancel} title="Remover">✕</button>
                </div>
            )}

            <div className="wa-input-row">
                <input
                    ref={fileRef}
                    type="file"
                    accept={ACCEPT_MEDIA}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <button
                    className="wa-attach-btn"
                    title="Anexar arquivo"
                    onClick={() => fileRef.current?.click()}
                    disabled={sending}
                >
                    📎
                </button>

                {!mediaFile && (
                    <textarea
                        className="wa-input"
                        rows={1}
                        value={text}
                        onChange={e => onTextChange(e.target.value)}
                        onKeyDown={handleKey}
                        placeholder="Digite uma mensagem..."
                        disabled={sending}
                    />
                )}
                {mediaFile && (
                    <span className="wa-input-placeholder">Arquivo pronto para envio</span>
                )}

                <button
                    className="wa-btn primary"
                    onClick={handleSubmit}
                    disabled={sending || (!text.trim() && !mediaFile)}
                >
                    Enviar
                </button>
            </div>
        </div>
    );
}

// ─── ChatView ────────────────────────────────────────────────────────────────

function ChatView({ chat, onAssumed, onClosed, onMessagesChange }) {
    const { hasUserPermission } = usePermission();
    const canAttend = hasUserPermission(UserPermission.WHATSAPP_ATTEND);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const bottomRef = useRef(null);

    const loadMessages = useCallback(() => {
        makeRequest(`${api}/api/whatsapp/chats/${chat.id}/messages`, 'GET')
            .then(setMessages)
            .catch(() => { });
    }, [chat.id]);

    useEffect(() => {
        setMessages([]);
        setText('');
        setError(null);
        loadMessages();
    }, [chat.id, loadMessages]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // poll for new messages every 3s
    useEffect(() => {
        const interval = setInterval(loadMessages, 3000);
        return () => clearInterval(interval);
    }, [loadMessages]);

    const handleAssume = async () => {
        try {
            const updated = await makeRequest(`${api}/api/whatsapp/chats/${chat.id}/assume`, 'POST');
            onAssumed(updated);
        } catch (e) {
            setError('Erro ao assumir: ' + e.message);
        }
    };

    const handleClose = async () => {
        if (!window.confirm('Encerrar este atendimento?')) return;
        try {
            const updated = await makeRequest(`${api}/api/whatsapp/chats/${chat.id}/close`, 'POST');
            onClosed(updated);
        } catch (e) {
            setError('Erro ao encerrar: ' + e.message);
        }
    };

    const handleSend = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        setError(null);
        try {
            await makeRequest(`${api}/api/whatsapp/chats/${chat.id}/messages`, 'POST', { 'Content-Type': 'application/json' }, JSON.stringify({ text: text.trim() }));
            setText('');
            loadMessages();
            onMessagesChange?.();
        } catch (e) {
            setError('Erro ao enviar: ' + e.message);
            setTimeout(() => setError(null), 5000);
        } finally {
            setSending(false);
        }
    };

    const handleSendMedia = useCallback(async (file) => {
        setSending(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const token = getAuthToken();
            const res = await fetch(`${api}/api/whatsapp/chats/${chat.id}/media`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            if (!res.ok) throw new Error(await res.text());
            loadMessages();
            onMessagesChange?.();
        } catch (e) {
            setError('Erro ao enviar mídia: ' + e.message);
            setTimeout(() => setError(null), 5000);
        } finally {
            setSending(false);
        }
    }, [api, chat.id, loadMessages, onMessagesChange]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    };

    const isWaiting = chat.status === 'WAITING';
    const isInProgress = chat.status === 'IN_PROGRESS';
    const isClosed = chat.status === 'CLOSED';

    return (
        <div className="wa-chat-view">
            {/* Header */}
            <div className="wa-chat-header">
                <div className="wa-chat-header-info">
                    <div className="wa-avatar sm" style={{ background: avatarColor(chat.phone) }}>
                        {getInitials(chat.contactName || chat.phone)}
                    </div>
                    <div>
                        <div className="wa-chat-header-name">{chat.contactName || chat.phone}</div>
                        <div className="wa-chat-header-phone">{chat.phone}</div>
                    </div>
                </div>
                <div className="wa-chat-header-actions">
                    {isWaiting && canAttend && (
                        <button className="wa-btn primary" onClick={handleAssume}>
                            Assumir atendimento
                        </button>
                    )}
                    {isInProgress && canAttend && (
                        <>
                            <span className="wa-attendant">Atendente: {chat.attendantUserName}</span>
                            <button className="wa-btn danger" onClick={handleClose}>
                                Encerrar
                            </button>
                        </>
                    )}
                    {isClosed && <span className="wa-status-tag closed">Encerrado</span>}
                </div>
            </div>

            {/* Messages */}
            <div className="wa-messages">
                {messages.length === 0 && (
                    <div className="wa-no-msgs">Nenhuma mensagem</div>
                )}
                {messages.map(msg => <MessageBubble key={msg.id} msg={msg} />)}
                <div ref={bottomRef} />
            </div>

            {error && <div className="wa-error">{error}</div>}

            {/* Input */}
            {isInProgress && canAttend && (
                <MessageInput
                    text={text}
                    onTextChange={setText}
                    onKeyDown={handleKeyDown}
                    onSend={handleSend}
                    onSendMedia={handleSendMedia}
                    sending={sending}
                />
            )}

            {isWaiting && (
                <div className="wa-waiting-bar">
                    {canAttend
                        ? 'Clique em "Assumir atendimento" para começar a responder.'
                        : 'Aguardando um atendente assumir esta conversa.'}
                </div>
            )}
        </div>
    );
}

// ─── WhatsappPage (main) ─────────────────────────────────────────────────────

export default function WhatsappPage() {
    const { hasUserPermission, hasCompanyPermission } = usePermission();

    const canView = hasUserPermission(UserPermission.WHATSAPP_VIEW);

    const [chats, setChats] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [tab, setTab] = useState('WAITING'); // WAITING | IN_PROGRESS | CLOSED
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const loadChats = useCallback(() => {
        setLoading(true);
        makeRequest(`${api}/api/whatsapp/chats`, 'GET')
            .then(data => setChats(data))
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { loadChats(); }, [loadChats]);

    // poll every 5s
    useEffect(() => {
        const interval = setInterval(loadChats, 5000);
        return () => clearInterval(interval);
    }, [loadChats]);

    // refresh active chat when chats update
    useEffect(() => {
        if (activeChat) {
            const updated = chats.find(c => c.id === activeChat.id);
            if (updated) setActiveChat(updated);
        }
    }, [chats]);

    if (!canView) {
        return (
            <div className="wa-unauthorized">
                Você não tem permissão para acessar o módulo de WhatsApp.
            </div>
        );
    }

    const waiting = chats.filter(c => c.status === 'WAITING');
    const inProgress = chats.filter(c => c.status === 'IN_PROGRESS');
    const closed = chats.filter(c => c.status === 'CLOSED');

    const tabChats = tab === 'WAITING' ? waiting : tab === 'IN_PROGRESS' ? inProgress : closed;

    const filtered = search.trim()
        ? tabChats.filter(c =>
            (c.contactName || '').toLowerCase().includes(search.toLowerCase()) ||
            c.phone.includes(search))
        : tabChats;

    return (
        <div className="wa-page">
            {/* Left panel */}
            <div className="wa-left">
                <div className="wa-left-header">
                    <h2>WhatsApp</h2>
                    <button className="wa-refresh-btn" onClick={loadChats} title="Atualizar">↻</button>
                </div>

                <input
                    className="wa-search"
                    type="text"
                    placeholder="Pesquisar..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />

                <div className="wa-tabs">
                    <button
                        className={`wa-tab ${tab === 'WAITING' ? 'active' : ''}`}
                        onClick={() => setTab('WAITING')}
                    >
                        Aguardando
                        {waiting.length > 0 && <span className="wa-badge sm">{waiting.length}</span>}
                    </button>
                    <button
                        className={`wa-tab ${tab === 'IN_PROGRESS' ? 'active' : ''}`}
                        onClick={() => setTab('IN_PROGRESS')}
                    >
                        Em atendimento
                    </button>
                    <button
                        className={`wa-tab ${tab === 'CLOSED' ? 'active' : ''}`}
                        onClick={() => setTab('CLOSED')}
                    >
                        Encerrados
                    </button>
                </div>

                <div className="wa-chat-list">
                    {loading && chats.length === 0 && (
                        <div className="wa-list-empty">Carregando...</div>
                    )}
                    {!loading && filtered.length === 0 && (
                        <div className="wa-list-empty">
                            {tab === 'WAITING' ? 'Nenhum atendimento aguardando' :
                                tab === 'IN_PROGRESS' ? 'Nenhum atendimento em andamento' :
                                    'Nenhum atendimento encerrado'}
                        </div>
                    )}
                    {filtered.map(chat => (
                        <ChatItem
                            key={chat.id}
                            chat={chat}
                            active={activeChat?.id === chat.id}
                            onClick={() => setActiveChat(chat)}
                        />
                    ))}
                </div>
            </div>

            {/* Right panel */}
            <div className="wa-right">
                {activeChat ? (
                    <ChatView
                        key={activeChat.id}
                        chat={activeChat}
                        onAssumed={(updated) => {
                            setActiveChat(updated);
                            loadChats();
                        }}
                        onClosed={(updated) => {
                            setActiveChat(updated);
                            loadChats();
                        }}
                    />
                ) : (
                    <div className="wa-empty-state">
                        <div className="wa-empty-icon">💬</div>
                        <p>Selecione um atendimento para visualizar a conversa</p>
                        {waiting.length > 0 && (
                            <p className="wa-empty-hint">
                                {waiting.length} atendimento{waiting.length > 1 ? 's' : ''} aguardando na fila
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
